import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  orderBy
} from 'firebase/firestore';

import { db } from '../utils/firebase';

const RELATIONSHIPS_COLLECTION = 'careerCoachRelationships';

export const RELATIONSHIP_STATUS = {
  PENDING: 'Pending',
  ACTIVE: 'Active',
  DECLINED: 'Declined',
  ENDED: 'Ended'
};

export const RELATIONSHIP_VIEW_STATE = {
  NONE: 'none',
  COACH_INVITED: 'coach_invited',
  CANDIDATE_REQUESTED: 'candidate_requested',
  ACTIVE: 'active',
  DECLINED: 'declined',
  ENDED: 'ended',
  PENDING: 'pending'
};

export function relationshipDocId(candidateUid, coachUid) {
  return `${candidateUid}_${coachUid}`;
}

export function relationshipRef(candidateUid, coachUid) {
  return doc(
    db,
    RELATIONSHIPS_COLLECTION,
    relationshipDocId(candidateUid, coachUid)
  );
}

function isTerminalStatus(status) {
  return status === RELATIONSHIP_STATUS.DECLINED ||
    status === RELATIONSHIP_STATUS.ENDED;
}

function isDeterministicRelationshipId(relationshipId, data = {}) {
  if (!relationshipId || !data.candidateUid || !data.coachUid) {
    return false;
  }

  return relationshipId === relationshipDocId(data.candidateUid, data.coachUid);
}

function dedupeRelationships(docs = []) {
  const byPair = new Map();

  docs.forEach((relationship) => {
    const key = relationshipDocId(
      relationship.candidateUid,
      relationship.coachUid
    );
    const existing = byPair.get(key);

    if (!existing || relationship.id === key) {
      byPair.set(key, relationship);
    }
  });

  return Array.from(byPair.values());
}

function computeRelationshipStatus(flags = {}) {
  const bothSidesEngaged =
    (flags.coachInvited || flags.coachAccepted) &&
    (flags.candidateRequested || flags.candidateAccepted);

  if (bothSidesEngaged) {
    return {
      status: RELATIONSHIP_STATUS.ACTIVE,
      active: true
    };
  }

  return {
    status: RELATIONSHIP_STATUS.PENDING,
    active: false
  };
}

export function getRelationshipViewState(relationship) {
  if (!relationship) {
    return RELATIONSHIP_VIEW_STATE.NONE;
  }

  if (relationship.status === RELATIONSHIP_STATUS.DECLINED) {
    return RELATIONSHIP_VIEW_STATE.DECLINED;
  }

  if (relationship.status === RELATIONSHIP_STATUS.ENDED) {
    return RELATIONSHIP_VIEW_STATE.ENDED;
  }

  if (relationship.active === true && relationship.status === RELATIONSHIP_STATUS.ACTIVE) {
    return RELATIONSHIP_VIEW_STATE.ACTIVE;
  }

  if (relationship.coachInvited && !relationship.candidateAccepted) {
    return RELATIONSHIP_VIEW_STATE.COACH_INVITED;
  }

  if (relationship.candidateRequested && !relationship.coachAccepted) {
    return RELATIONSHIP_VIEW_STATE.CANDIDATE_REQUESTED;
  }

  return RELATIONSHIP_VIEW_STATE.PENDING;
}

export function getRelationshipStatusLabel(viewState) {
  switch (viewState) {
    case RELATIONSHIP_VIEW_STATE.COACH_INVITED:
      return 'Invitation from coach';
    case RELATIONSHIP_VIEW_STATE.CANDIDATE_REQUESTED:
      return 'Request sent — awaiting coach';
    case RELATIONSHIP_VIEW_STATE.ACTIVE:
      return 'Active coaching relationship';
    case RELATIONSHIP_VIEW_STATE.DECLINED:
      return 'Declined';
    case RELATIONSHIP_VIEW_STATE.ENDED:
      return 'Relationship ended';
    case RELATIONSHIP_VIEW_STATE.PENDING:
      return 'Pending';
    default:
      return 'No relationship';
  }
}

export async function getRelationship(candidateUid, coachUid) {
  if (!candidateUid || !coachUid) {
    return null;
  }

  try {
    const snap = await getDoc(relationshipRef(candidateUid, coachUid));

    if (snap.exists()) {
      return {
        id: snap.id,
        ...snap.data()
      };
    }
  } catch (error) {
    console.error('getRelationship error:', error);
  }

  // Fall back to list query for legacy auto-ID documents not yet migrated.
  try {
    const relationships = await listRelationshipsForUser({
      uid: candidateUid,
      candidate: true
    });

    return relationships.find(rel => rel.coachUid === coachUid) || null;
  } catch (error) {
    console.error('getRelationship fallback error:', error);
    return null;
  }
}

export async function getCandidateRelationships(candidateUid) {
  return listRelationshipsForUser({
    uid: candidateUid,
    candidate: true
  });
}

export async function getCoachRelationships(coachUid) {
  return listRelationshipsForUser({
    uid: coachUid,
    coach: true
  });
}

export async function getActiveCandidateRelationship(candidateUid) {
  const relationships = await getCandidateRelationships(candidateUid);

  return relationships.find(rel =>
    rel.active === true &&
    rel.status === RELATIONSHIP_STATUS.ACTIVE
  ) || null;
}

export async function listRelationshipsForUser({
  uid,
  coach = false,
  candidate = false,
  admin = false
}) {
  if (!uid) return [];

  try {
    let constraints = [];

    if (admin) {
      constraints = [];
    } else if (coach) {
      constraints = [where('coachUid', '==', uid)];
    } else if (candidate) {
      constraints = [where('candidateUid', '==', uid)];
    } else {
      return [];
    }

    const snapshot = await getDocs(
      query(
        collection(db, RELATIONSHIPS_COLLECTION),
        ...constraints,
        orderBy('updatedAt', 'desc')
      )
    );

    const relationships = snapshot.docs.map(relDoc => ({
      id: relDoc.id,
      ...relDoc.data()
    }));

    return dedupeRelationships(relationships);
  } catch (error) {
    console.error('listRelationshipsForUser error:', error);
    return [];
  }
}

export async function createRelationship({
  candidateUid,
  coachUid,
  candidateName = '',
  candidateEmail = '',
  coachName = '',
  coachEmail = '',
  candidateRequested = false,
  candidateAccepted = false,
  coachInvited = false,
  coachAccepted = false,
  resetTerminal = false
}) {
  const ref = relationshipRef(candidateUid, coachUid);
  let existing = {};

  try {
    const existingSnap = await getDoc(ref);
    existing = existingSnap.exists() ? existingSnap.data() : {};
  } catch (error) {
    console.warn('createRelationship: could not read existing doc, creating fresh', error);
  }

  const legacyRelationship = await getRelationship(candidateUid, coachUid);
  let legacyData = {};
  if (legacyRelationship) {
    const { id: _legacyId, ...rest } = legacyRelationship;
    legacyData = rest;
  }
  const terminalStatus = existing.status || legacyRelationship?.status;
  const base = resetTerminal && isTerminalStatus(terminalStatus)
    ? {}
    : { ...legacyData, ...existing };

  const flags = {
    candidateRequested: candidateRequested || base.candidateRequested || false,
    candidateAccepted: candidateAccepted || base.candidateAccepted || false,
    coachInvited: coachInvited || base.coachInvited || false,
    coachAccepted: coachAccepted || base.coachAccepted || false
  };

  const statusFields = computeRelationshipStatus(flags);

  const payload = {
    candidateUid,
    coachUid,
    candidateName: candidateName || base.candidateName || '',
    candidateEmail: candidateEmail || base.candidateEmail || '',
    coachName: coachName || base.coachName || '',
    coachEmail: coachEmail || base.coachEmail || '',
    ...flags,
    ...statusFields,
    updatedAt: serverTimestamp(),
    createdAt: base.createdAt || existing.createdAt || serverTimestamp()
  };

  if (statusFields.status === RELATIONSHIP_STATUS.ACTIVE && !base.startedAt) {
    payload.startedAt = serverTimestamp();
  }

  if (resetTerminal && isTerminalStatus(terminalStatus)) {
    payload.endedAt = null;
  }

  await setDoc(ref, payload, { merge: true });

  return {
    id: ref.id,
    ...payload
  };
}

export async function requestCoach({
  candidateUid,
  coachUid,
  candidateName = '',
  candidateEmail = '',
  coachName = '',
  coachEmail = ''
}) {
  const activeRelationship = await getActiveCandidateRelationship(candidateUid);

  if (activeRelationship && activeRelationship.coachUid !== coachUid) {
    throw new Error(
      'You already have an active career coach. End that relationship before selecting another coach.'
    );
  }

  const existing = await getRelationship(candidateUid, coachUid);

  if (existing && !isTerminalStatus(existing.status)) {
    if (existing.active && existing.status === RELATIONSHIP_STATUS.ACTIVE) {
      return existing;
    }

    if (existing.coachInvited) {
      return acceptRelationship({
        candidateUid,
        coachUid,
        candidateName,
        candidateEmail,
        coachName,
        coachEmail
      });
    }
  }

  return createRelationship({
    candidateUid,
    coachUid,
    candidateName,
    candidateEmail,
    coachName,
    coachEmail,
    candidateRequested: true,
    candidateAccepted: true,
    resetTerminal: true
  });
}

export async function inviteCandidate({
  candidateUid,
  coachUid,
  candidateName = '',
  candidateEmail = '',
  coachName = '',
  coachEmail = ''
}) {
  const activeRelationship = await getActiveCandidateRelationship(candidateUid);

  if (activeRelationship && activeRelationship.coachUid !== coachUid) {
    throw new Error('This client already has an active career coach.');
  }

  const existing = await getRelationship(candidateUid, coachUid);

  if (existing && !isTerminalStatus(existing.status)) {
    if (existing.active && existing.status === RELATIONSHIP_STATUS.ACTIVE) {
      return existing;
    }

    if (existing.candidateRequested || existing.candidateAccepted) {
      return createRelationship({
        candidateUid,
        coachUid,
        candidateName,
        candidateEmail,
        coachName,
        coachEmail,
        candidateRequested: existing.candidateRequested,
        candidateAccepted: existing.candidateAccepted,
        coachInvited: true,
        coachAccepted: true
      });
    }
  }

  return createRelationship({
    candidateUid,
    coachUid,
    candidateName,
    candidateEmail,
    coachName,
    coachEmail,
    coachInvited: true,
    coachAccepted: true,
    resetTerminal: true
  });
}

export async function acceptRelationship({
  candidateUid,
  coachUid,
  candidateName = '',
  candidateEmail = '',
  coachName = '',
  coachEmail = ''
}) {
  const existing = await getRelationship(candidateUid, coachUid);

  if (!existing || isTerminalStatus(existing.status)) {
    throw new Error('No pending coaching invitation to accept.');
  }

  return createRelationship({
    candidateUid,
    coachUid,
    candidateName,
    candidateEmail,
    coachName,
    coachEmail,
    candidateRequested: existing.candidateRequested,
    candidateAccepted: true,
    coachInvited: existing.coachInvited,
    coachAccepted: existing.coachAccepted || existing.coachInvited
  });
}

export async function declineRelationship({
  candidateUid,
  coachUid
}) {
  const ref = relationshipRef(candidateUid, coachUid);

  await updateDoc(ref, {
    status: RELATIONSHIP_STATUS.DECLINED,
    active: false,
    updatedAt: serverTimestamp()
  });

  return getRelationship(candidateUid, coachUid);
}

export async function endRelationship({
  candidateUid,
  coachUid
}) {
  const existing = await getRelationship(candidateUid, coachUid);

  if (!existing || existing.status !== RELATIONSHIP_STATUS.ACTIVE) {
    throw new Error('No active coaching relationship to end.');
  }

  const ref = relationshipRef(candidateUid, coachUid);

  await updateDoc(ref, {
    status: RELATIONSHIP_STATUS.ENDED,
    active: false,
    endedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  return getRelationship(candidateUid, coachUid);
}

/** @deprecated Use requestCoach, inviteCandidate, or acceptRelationship */
export async function upsertPendingRelationship(params) {
  if (params.coachInvited) {
    return inviteCandidate(params);
  }

  return requestCoach(params);
}

export async function updateRelationshipById(relationshipId, updates) {
  await updateDoc(
    doc(db, RELATIONSHIPS_COLLECTION, relationshipId),
    {
      ...updates,
      updatedAt: serverTimestamp()
    }
  );
}

export async function updateRelationshipPair(candidateUid, coachUid, updates) {
  const ref = relationshipRef(candidateUid, coachUid);
  await setDoc(
    ref,
    {
      ...updates,
      candidateUid,
      coachUid,
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );
}

/**
 * Copies legacy auto-ID relationship docs to deterministic IDs.
 * Legacy docs are deleted only when isAdmin is true.
 */
export async function migrateLegacyRelationships({
  uid,
  isAdmin = false
}) {
  if (!uid) return [];

  const allDocs = new Map();

  if (isAdmin) {
    const fullSnapshot = await getDocs(
      collection(db, RELATIONSHIPS_COLLECTION)
    ).catch(() => ({ docs: [] }));

    fullSnapshot.docs.forEach((relDoc) => {
      allDocs.set(relDoc.id, relDoc);
    });
  } else {
    const [coachSnapshot, candidateSnapshot] = await Promise.all([
      getDocs(
        query(
          collection(db, RELATIONSHIPS_COLLECTION),
          where('coachUid', '==', uid)
        )
      ).catch(() => ({ docs: [] })),
      getDocs(
        query(
          collection(db, RELATIONSHIPS_COLLECTION),
          where('candidateUid', '==', uid)
        )
      ).catch(() => ({ docs: [] }))
    ]);

    [...coachSnapshot.docs, ...candidateSnapshot.docs].forEach((relDoc) => {
      allDocs.set(relDoc.id, relDoc);
    });
  }

  const migratedIds = [];

  for (const relDoc of allDocs.values()) {
    const data = relDoc.data();
    const targetId = relationshipDocId(data.candidateUid, data.coachUid);

    if (!targetId || targetId.includes('undefined')) {
      continue;
    }

    if (isDeterministicRelationshipId(relDoc.id, data)) {
      continue;
    }

    const targetRef = doc(db, RELATIONSHIPS_COLLECTION, targetId);
    const targetSnap = await getDoc(targetRef);
    const targetData = targetSnap.exists() ? targetSnap.data() : {};

    await setDoc(
      targetRef,
      {
        ...data,
        ...targetData,
        candidateUid: data.candidateUid,
        coachUid: data.coachUid,
        updatedAt: serverTimestamp(),
        createdAt: data.createdAt || targetData.createdAt || serverTimestamp()
      },
      { merge: true }
    );

    migratedIds.push(targetId);

    if (isAdmin && relDoc.id !== targetId) {
      await deleteDoc(relDoc.ref);
    }
  }

  return migratedIds;
}
